import { ReactNode } from "react";
import { Box, Stack, Paper, SxProps, Theme } from "@mui/material";
import { useTranslation } from "next-i18next";
import { Button } from "src/components-mui/base/button/button";
import { Typography } from "src/components-mui/base/typography/typography";
import { WfHeading } from "src/components-mui/base/wf-heading/wf-heading";
import { useLinkBuilder } from "src/hooks/use-link-builder";

export interface IHideContentProps {
    title: string;
    description: string;
    containerHeight?: string | string[];
    hasBackgroundImage?: boolean;
    backgroundColor?: "white" | "whiteAlpha.800";
    topOffset?: string | string[];
    sx?: SxProps<Theme>;
    paperSx?: SxProps<Theme>;
    additionalContent?: ReactNode;
    dataTestId?: string;
    titleComponent?: "h2" | "h3" | "h4";
}

export const HIDE_CONTENT_TEST_ID = "hide-content";

export const HideContent = ({
    containerHeight = "auto",
    title,
    description,
    hasBackgroundImage = false,
    backgroundColor = "white",
    topOffset,
    sx,
    paperSx,
    additionalContent,
    dataTestId = HIDE_CONTENT_TEST_ID,
    titleComponent = "h2",
    children,
}: IHideContentProps & { children?: ReactNode }) => {
    const { buildLoginModalLink, buildRegisterModalLink } = useLinkBuilder();
    const { t } = useTranslation("common");

    return (
        <Box
            component="section"
            sx={{
                position: "relative",
                height: containerHeight,
                ...sx,
            }}
            data-test-id={dataTestId}
        >
            <Stack
                sx={{
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                {children}
                <Stack
                    sx={{
                        position: hasBackgroundImage ? "absolute" : "static",
                        width: "100%",
                        justifyContent: "center",
                        alignItems: "center",
                        top: hasBackgroundImage ? topOffset : undefined,
                        left: "0",
                        px: [2, 4, 4, 7],
                    }}
                >
                    <Paper
                        elevation={2}
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            p: [2, 3, 3, 5],
                            position: "relative",
                            backgroundColor,
                            ...paperSx,
                        }}
                    >
                        <WfHeading component={titleComponent} sx={{ textAlign: "center" }} fontSize={{ xs: "xl", sm: "2xl" }}>
                            {title}
                        </WfHeading>
                        <Typography
                            sx={{
                                textAlign: "center",
                                pt: [1, 1, 1, 2],
                                pb: [2, 3],
                            }}
                        >
                            {description}
                        </Typography>
                        <Stack
                            direction={["column-reverse", "row"]}
                            sx={{
                                justifyContent: "center",
                                alignItems: "center",
                                width: "100%",
                                gap: 2,
                            }}
                        >
                            <Button href={buildRegisterModalLink()} size="medium" variant="outlined" sx={{ width: { xs: "100%", sm: "auto" } }}>
                                {t("register")}
                            </Button>
                            <Button href={buildLoginModalLink()} size="medium" sx={{ width: { xs: "100%", sm: "auto" } }}>
                                {t("login-button")}
                            </Button>
                        </Stack>
                    </Paper>

                    {additionalContent ?? null}
                </Stack>
            </Stack>
        </Box>
    );
};
